import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { trackEvent, BOOKING_SCREENING_URL, BOOKING_THERAPY_URL, PHONE_NUMBER, PHONE_TEL } from '../lib/tracking';
import { optimizeImage } from '../lib/image';
import SatisfactionGuaranteeModal from '../components/SatisfactionGuaranteeModal';

// Local fallback data guaranteeing robust standalone rendering and instant speed for Google Ads
const FALLBACK_PROBLEMS = {
  stress: {
    titel: 'Stress',
    h1: 'Har stress fået for stor indflydelse på dit liv?',
    hero_undertekst: 'Få hjælp til at bryde stressmønstre, berolige nervesystemet og genopbygge dit overskud med professionel samtaleterapi.',
    genkendelse: `
      <p class="mb-3">Oplever du konstant uro, søvnproblemer, udmattelse eller følelsen af at være “på” hele tiden?</p>
      <p class="mb-3">Mange, der kæmper med stress, oplever et vedvarende alarmberedskab i kroppen: tankemylder der holder dig vågen om natten, spændinger i nakke og mave, og en følelse af, at selv små hverdagsopgaver kræver uforholdsmæssig stor energi.</p>
      <p>Forløb kan være særligt relevante ved arbejdsrelateret stress, udbrændthed eller følelsen af at bære et urimeligt stort overansvar for andre.</p>
    `,
    oensket_situation: `
      <p class="mb-3">Forestil dig en hverdag, hvor skuldrene er nede, vejrtrækningen er rolig, og du kan lægge arbejdet fra dig med god samvittighed.</p>
      <p>En tilstand, hvor du kan mærke dine egne grænser i tide, har overskud til dine relationer og kan nyde hverdagens øjeblikke uden den konstante indre uro.</p>
    `,
    loesning: `
      <p class="mb-3">Gennem samtaleterapi og kropslig regulering undersøger vi dine specifikke stressmønstre. Vi arbejder målrettet med at berolige nervesystemet, afdække udløserne og genopbygge balance.</p>
      <p>Mit udgangspunkt er, at du er mere end dine symptomer. Terapien foregår i dit eget tempo med respekt, varme og høj faglighed, hvor vi inddrager viden om hjernen og nervesystemet.</p>
    `,
    fordele: `
      <ul class="space-y-2.5">
        <li class="flex items-start gap-2.5"><i class="fa-solid fa-check text-emerald-600 mt-1 shrink-0"></i><span>Mulighed for at berolige et overbelastet nervesystem og finde ro i kroppen</span></li>
        <li class="flex items-start gap-2.5"><i class="fa-solid fa-check text-emerald-600 mt-1 shrink-0"></i><span>Større klarhed over dine personlige grænser og prioriteringer</span></li>
        <li class="flex items-start gap-2.5"><i class="fa-solid fa-check text-emerald-600 mt-1 shrink-0"></i><span>Konkrete redskaber til at dæmpe tankemylder og forbedre søvnkvaliteten</span></li>
        <li class="flex items-start gap-2.5"><i class="fa-solid fa-check text-emerald-600 mt-1 shrink-0"></i><span>Genopbygning af dit mentale og kropslige overskud</span></li>
      </ul>
    `,
    testimonial: {
      name: 'Birgitte, 43 år',
      text: 'Kim har hjulpet mig med at genfinde min selvtillid og skabe ro i mit liv. Gennem hans varme og nærværende tilgang følte jeg mig tryg og forstået fra første session. Jeg har nu redskaberne til at håndtere udfordringer og se nye muligheder.',
    },
    faq: [
      {
        q: 'Hvor hurtigt kan jeg mærke en forandring ved stress?',
        a: 'Mange klienter oplever allerede efter første eller anden samtale en lettelse, fordi de får sat ord på presset og får enkle redskaber til at berolige nervesystemet.',
      },
      {
        q: 'Hvad koster et forløb ved stress?',
        a: 'En terapisession varer 60 min. og koster 1.200 kr. Et klippekort til 5 sessioner koster 5.000 kr. For studerende, unge under 25 og pensionister er prisen 800 kr.',
      },
      {
        q: 'Kan sessionerne foregå online?',
        a: 'Ja, sessionerne kan afholdes både fysisk i rolige rammer på Marengovej 2 på Amager eller online via en sikker videoforbindelse.',
      },
    ],
    proces_trin: [
      { step: '1', title: 'Gratis afklaringssamtale', subtitle: '20 min. uforpligtende', desc: 'Vi taler kort om din situation, dit stressniveau, og hvordan samtaleterapi kan give ro i kroppen.' },
      { step: '2', title: 'Første session', subtitle: '60 min. i trygt rum', desc: 'Vi afdækker dine stressudløsere og mønstre, beroliger nervesystemet og tilrettelægger en skræddersyet plan.' },
      { step: '3', title: 'Dit videre forløb', subtitle: 'I dit eget tempo', desc: 'Individuelt tilpasset samtaleterapi og kropslige øvelser, der skaber varig balance og genopbygger dit overskud.' },
    ],
    billede: 'https://assets.auxodev.dk/assets/ece5dc05-bafb-437f-b627-764ef307b8ad/img-8587.jpeg',
    meta_title: 'Stressbehandling København | Psykoterapeut Kim Palasz',
    meta_desc: 'Få hjælp til stress og udbrændthed hos Psykoterapeut Kim Palasz på Amager. Berolig nervesystemet og genfind overskuddet. Book gratis afklaring.',
  },

  angst: {
    titel: 'Angst',
    h1: 'Har angsten fået for stor magt over din hverdag?',
    hero_undertekst: 'Lær at forstå og regulere angsten i både krop og tanker, så du kan genvinde din frihed og tryghed i livet.',
    genkendelse: `
      <p class="mb-3">Angst kan opleves som hjertebanken, svimmelhed, kontroltab eller en følelse af at være ved at “gå i stykker”.</p>
      <p class="mb-3">Måske mærker du en konstant indre uro, bekymringstanker der kører i ring, eller du oplever panikanfald i specifikke situationer. Angst kan vise sig som præstationsangst, social angst, generaliseret angst eller panikangst.</p>
      <p>Når angsten fylder for meget, begynder du måske at undgå steder, mennesker eller opgaver, og din hverdag indsnævres gradvist.</p>
    `,
    oensket_situation: `
      <p class="mb-3">Forestil dig at kunne træde ind i rum og situationer med ro i brystet og tillid til dig selv.</p>
      <p>At have friheden tilbage til at sige ja til sociale oplevelser, karrieremuligheder og udfordringer uden at frygte for det næste angstanfald.</p>
    `,
    loesning: `
      <p class="mb-3">I terapien får du støtte til at forstå angsten og lære at regulere den – i kroppen såvel som i tankerne.</p>
      <p>Vi arbejder med samtaleterapi, neuroaffektiv forståelse og eventuelt hypnoterapi for at afvæbne de automatiske alarmsignaler. Du lærer at møde ubehaget uden at flygte fra det, så nervesystemet gradvist genfinder sin naturlige ro og stabilitet.</p>
    `,
    fordele: `
      <ul class="space-y-2.5">
        <li class="flex items-start gap-2.5"><i class="fa-solid fa-check text-emerald-600 mt-1 shrink-0"></i><span>Forståelse af angstens funktion, og hvad kroppen forsøger at fortælle dig</span></li>
        <li class="flex items-start gap-2.5"><i class="fa-solid fa-check text-emerald-600 mt-1 shrink-0"></i><span>Konkrete øvelser til at dæmpe panik, hjertebanken og kropslig uro</span></li>
        <li class="flex items-start gap-2.5"><i class="fa-solid fa-check text-emerald-600 mt-1 shrink-0"></i><span>Frihed til at deltage i sociale arrangementer og arbejdsliv uden undgåelsesadfærd</span></li>
        <li class="flex items-start gap-2.5"><i class="fa-solid fa-check text-emerald-600 mt-1 shrink-0"></i><span>Styrket tillid til egen krop og sind</span></li>
      </ul>
    `,
    testimonial: {
      name: 'Josefine, 26 år',
      text: 'Efter et forløb hos Kim har jeg lært at tackle min angst og ændre negative mønstre. Hans evne til at stille de rigtige spørgsmål og støtte mig i processen har gjort en stor forskel. Jeg føler mig stærkere og mere i balance.',
    },
    faq: [
      {
        q: 'Hvordan adskiller samtaleterapi og hypnose sig ved angst?',
        a: 'Samtaleterapi giver dig dyb indsigt i angstens mekanismer og udløsere, mens hypnoterapi kan arbejde direkte med de ubevidste overbevisninger for hurtigere at dæmpe frygtresponsen.',
      },
      {
        q: 'Hvor mange sessioner skal jeg forvente ved angst?',
        a: 'Det er individuelt, men mange klienter oplever mærkbare fremskridt og større ro efter 3-6 sessioner.',
      },
      {
        q: 'Hvad gør vi, hvis jeg bliver bange undervejs?',
        a: 'Sessionen foregår i et 100% trygt rum. Vi arbejder i dit tempo og anvender kropslige forankrings- og vejrtrækningsøvelser.',
      },
    ],
    proces_trin: [
      { step: '1', title: 'Gratis afklaringssamtale', subtitle: '20 min. uforpligtende', desc: 'Vi taler om dine angstsymptomer, hvad der udløser uroen, og sikrer at metoden passer til dig.' },
      { step: '2', title: 'Første session', subtitle: '60 min. i trygt rum', desc: 'Vi kortlægger angstens mekanismer og etablerer de første kropslige og tankemæssige beroligende teknikker.' },
      { step: '3', title: 'Dit videre forløb', subtitle: 'I dit eget tempo', desc: 'Målrettet arbejde med nervesystemets tryghed, gradvis eksponering og frihed fra undgåelsesadfærd.' },
    ],
    billede: 'https://assets.auxodev.dk/assets/ece5dc05-bafb-437f-b627-764ef307b8ad/profile-img.png',
    meta_title: 'Angstbehandling København | Psykoterapeut Kim Palasz',
    meta_desc: 'Professionel behandling af angst og panik hos Psykoterapeut Kim Palasz på Amager. Lær at regulere kroppen og genfind friheden. Book gratis samtale.',
  },

  depression: {
    titel: 'Depression',
    h1: 'Føles hverdagen tung, uoverskuelig eller meningsløs?',
    hero_undertekst: 'Få støtte og nærværende samtaleterapi til at genfinde livsglæden, meningen og kontakten til dine egne ressourcer.',
    genkendelse: `
      <p class="mb-3">Føles hverdagen tung, grå og drænende? Har du mistet lysten til det, der før gav dig glæde, eller oplever du en dyb indre udmattelse og tomhed?</p>
      <p class="mb-3">Depression og nedtrykthed kan få selv små gøremål til at virke uoverstigelige og efterlade dig med en følelse af utilstrækkelighed, skyld og ensomhed.</p>
      <p>Det er helt normalt at trække sig fra relationer, når energien svigter, men det kan forstærke isolationen.</p>
    `,
    oensket_situation: `
      <p class="mb-3">At vågne op med en følelse af håb og mening for dagen foran dig.</p>
      <p>At mærke glæde ved hverdagens små øjeblikke, have overskud til dine kære og føle dig forbundet med livet og dig selv igen.</p>
    `,
    loesning: `
      <p class="mb-3">I et trygt og ufordømmende rum arbejder vi sammen om at forstå, hvad der har udløst nedtryktheden. Vi undersøger de underliggende følelser, mønstre og uforløste behov, og tager små, realistiske skridt mod fornyet energi.</p>
      <p>Terapien handler ikke om at tvinge et kunstigt positivt sind frem, men om ærlig accept og skånsom genetablering af forbindelsen til dine ressourcer.</p>
    `,
    fordele: `
      <ul class="space-y-2.5">
        <li class="flex items-start gap-2.5"><i class="fa-solid fa-check text-emerald-600 mt-1 shrink-0"></i><span>Et trygt rum, hvor du ikke behøver tage en facade på</span></li>
        <li class="flex items-start gap-2.5"><i class="fa-solid fa-check text-emerald-600 mt-1 shrink-0"></i><span>Gradvis genopdagelse af glæde, mening og handlekraft</span></li>
        <li class="flex items-start gap-2.5"><i class="fa-solid fa-check text-emerald-600 mt-1 shrink-0"></i><span>Støtte til at bryde isolation og opbygge overskuelige rutiner</span></li>
        <li class="flex items-start gap-2.5"><i class="fa-solid fa-check text-emerald-600 mt-1 shrink-0"></i><span>Forståelse for de følelser og tab, der kan ligge bag nedtryktheden</span></li>
      </ul>
    `,
    testimonial: {
      name: 'Søren, 52 år',
      text: 'Jeg fik hjælp til at finde klarhed i en svær tid, hvor jeg følte mig fastlåst. Kims indsigt og guidning har ikke kun løst de problemer, jeg kom med, men også givet mig værktøjer til at skabe forandringer i mit liv. Jeg kan varmt anbefale ham.',
    },
    faq: [
      {
        q: 'Er samtaleterapi nok ved depression?',
        a: 'Ved mild til moderat depression og modløshed er samtaleterapi veldokumenteret. Ved svær klinisk depression anbefales altid sideløbende kontakt med egen læge.',
      },
      {
        q: 'Hvad hvis jeg ikke har energi til at tale ret meget?',
        a: 'Det er helt forventeligt. Vi tilpasser sessionen, så du ikke overbelastes, og arbejder med nærvær og små overskuelige skridt.',
      },
    ],
    proces_trin: [
      { step: '1', title: 'Gratis afklaringssamtale', subtitle: '20 min. uforpligtende', desc: 'En rolig snak om, hvor du står lige nu – helt uden krav, forventninger eller præstation.' },
      { step: '2', title: 'Første session', subtitle: '60 min. i trygt rum', desc: 'Vi skaber et trygt rum, kortlægger hverdagens ressourcer og de underliggende følelser og behov.' },
      { step: '3', title: 'Dit videre forløb', subtitle: 'I dit eget tempo', desc: 'Nænsomt arbejde med accept, meningsfuldhed, struktur og gradvis genopbygning af livsglæde.' },
    ],
    billede: 'https://assets.auxodev.dk/assets/ece5dc05-bafb-437f-b627-764ef307b8ad/img-4513.jpg',
    meta_title: 'Hjælp ved depression og nedtrykthed | Kim Palasz',
    meta_desc: 'Få støtte ved depression, modløshed og tomhed hos Psykoterapeut Kim Palasz i København. Nærværende samtaleterapi. Book gratis afklaringssamtale.',
  },

  'lavt-selvvaerd': {
    titel: 'Lavt selvværd',
    h1: 'Styrer selvkritik og tvivl din oplevelse af dig selv?',
    hero_undertekst: 'Opbyg et sundt og stabilt selvværd, lær at stå ved hvem du er, og mød dig selv med accept og styrke.',
    genkendelse: `
      <p class="mb-3">Kæmper du med en konstant indre kritiker, der fortæller dig, at du ikke er god nok, dygtig nok eller værd at elske?</p>
      <p class="mb-3">Mange med lavt selvværd tilpasser sig andre på bekostning af egne behov, frygter at blive afvist eller afsløret som utilstrækkelige, eller føler at de hele tiden skal præstere for at have berettigelse.</p>
      <p>Det kan skabe udmattelse og en følelse af, at du aldrig rigtig kan slappe af og være dig selv.</p>
    `,
    oensket_situation: `
      <p class="mb-3">At kunne hvile i den du er, med ro i maven og rank ryg.</p>
      <p>At turde udtrykke dine meninger, sætte sunde grænser og modtage anerkendelse og kærlighed uden at tvivle på dit værd.</p>
    `,
    loesning: `
      <p class="mb-3">Gennem samtaleterapi og NLP arbejder vi med at afdække de gamle erfaringer og overbevisninger, der har formet dit selvbillede.</p>
      <p>Vi styrker din kontakt til dine kerneværdier og egne ressourcer, så du kan erstatte selvkritik med indre stabilitet, varme og selvrespekt.</p>
    `,
    fordele: `
      <ul class="space-y-2.5">
        <li class="flex items-start gap-2.5"><i class="fa-solid fa-check text-emerald-600 mt-1 shrink-0"></i><span>Mindre selvkritik og større venlighed over for egne fejl og grænser</span></li>
        <li class="flex items-start gap-2.5"><i class="fa-solid fa-check text-emerald-600 mt-1 shrink-0"></i><span>Evnen til at sige fra uden frygt for afvisning eller dårlig samvittighed</span></li>
        <li class="flex items-start gap-2.5"><i class="fa-solid fa-check text-emerald-600 mt-1 shrink-0"></i><span>Frihed til at være dig selv uden konstant at skulle bevise dit værd</span></li>
        <li class="flex items-start gap-2.5"><i class="fa-solid fa-check text-emerald-600 mt-1 shrink-0"></i><span>Stærkere, tryggere og mere ligeværdige relationer</span></li>
      </ul>
    `,
    testimonial: {
      name: 'Birgitte, 43 år',
      text: 'Kim har hjulpet mig med at genfinde min selvtillid og skabe ro i mit liv. Gennem hans varme og nærværende tilgang følte jeg mig tryg og forstået fra første session. Jeg har nu redskaberne til at håndtere udfordringer og se nye muligheder.',
    },
    faq: [
      {
        q: 'Hvad er forskellen på selvtillid og selvværd?',
        a: 'Selvtillid handler om, hvad du kan præstere og gøre, mens selvværd handler om den grundlæggende følelse af din værdi som menneske, uafhængigt af dine præstationer.',
      },
      {
        q: 'Kan hypnose hjælpe på selvværd?',
        a: 'Ja, hypnose er særdeles velegnet til at ændre dybt indgroede overbevisninger om utilstrækkelighed og styrke dit indre billede af dig selv.',
      },
    ],
    proces_trin: [
      { step: '1', title: 'Gratis afklaringssamtale', subtitle: '20 min. uforpligtende', desc: 'En åben, uforpligtende samtale om dine oplevelser med selvkritik, tvivl og relationer.' },
      { step: '2', title: 'Første session', subtitle: '60 min. i trygt rum', desc: 'Vi finder rødderne til de negative overbevisninger og definerer de sunde mål for dit selvbillede.' },
      { step: '3', title: 'Dit videre forløb', subtitle: 'I dit eget tempo', desc: 'Konkret træning i sunde grænser, selvaccept og opbygning af et robust og stabilt selvværd.' },
    ],
    billede: 'https://assets.auxodev.dk/assets/ece5dc05-bafb-437f-b627-764ef307b8ad/img-1647.jpeg',
    meta_title: 'Lavt selvværd og selvtillid | Psykoterapeut Kim Palasz',
    meta_desc: 'Styrk dit selvværd og overvind selvkritik hos Psykoterapeut Kim Palasz på Amager i København. Nærværende terapi. Book en gratis samtale.',
  },

  traumer: {
    titel: 'Traumer',
    h1: 'Sidder gamle oplevelser stadig fast i krop og sind?',
    hero_undertekst: 'Få professionel traumeterapi og skånsom forløsning af chok, svigt og overvældelse hos erfaren traumeterapeut MPF.',
    genkendelse: `
      <p class="mb-3">Uforløste traumer kan påvirke nervesystemet og din hverdag mere, end du tror.</p>
      <p class="mb-3">Måske mærker du pludselige kropslige reaktioner, følelsesmæssig nedlukning, mareridt eller en dyb mistillid til omgivelserne. Typiske temaer kan være barndomstraumer, ulykker, overgreb, pludselige tab eller svigt, der har efterladt kroppen i et permanent forsvar.</p>
      <p>Kroppen husker det, som hovedet måske prøver at glemme.</p>
    `,
    oensket_situation: `
      <p class="mb-3">At fortiden endelig føles som fortid – ikke som noget, der udspiller sig i din krop lige nu.</p>
      <p>At føle dig tryg i egen krop, kunne åbne op for sunde relationer og leve dit liv med frihed, ro og overskud.</p>
    `,
    loesning: `
      <p class="mb-3">Traumeterapi hjælper dig med at bearbejde chok, svigt og overvældelse – og genskabe tryghed og livsglæde.</p>
      <p>Jeg er uddannet i Transbiologisk Traume Terapi (TBT) og trækker på solid neurobiologisk viden for at hjælpe nervesystemet med at slippe alarmen i et trygt, kontrolleret tempo uden overvældelse.</p>
    `,
    fordele: `
      <ul class="space-y-2.5">
        <li class="flex items-start gap-2.5"><i class="fa-solid fa-check text-emerald-600 mt-1 shrink-0"></i><span>Skånsom bearbejdning af chok, svigt og overvældelse uden retraumatiserende pres</span></li>
        <li class="flex items-start gap-2.5"><i class="fa-solid fa-check text-emerald-600 mt-1 shrink-0"></i><span>Frigørelse af fastlåst energi og kroniske spændinger i kroppen</span></li>
        <li class="flex items-start gap-2.5"><i class="fa-solid fa-check text-emerald-600 mt-1 shrink-0"></i><span>Reduktion af flashbacks, mareridt og konstant alarmberedskab</span></li>
        <li class="flex items-start gap-2.5"><i class="fa-solid fa-check text-emerald-600 mt-1 shrink-0"></i><span>Genskabelse af tryghed, livsglæde og kropslig ro</span></li>
      </ul>
    `,
    testimonial: {
      name: 'Josefine, 26 år',
      text: 'Efter et forløb hos Kim har jeg lært at tackle min angst og ændre negative mønstre. Hans evne til at stille de rigtige spørgsmål og støtte mig i processen har gjort en stor forskel. Jeg føler mig stærkere og mere i balance.',
    },
    faq: [
      {
        q: 'Hvad er Transbiologisk Traume Terapi (TBT)?',
        a: 'TBT er en avanceret traumeforløsende metode, der arbejder med forbindelsen mellem krop, sind og nervesystem til skånsomt at forløse chok og fastlåste oplevelser uden at retraumatiseres.',
      },
      {
        q: 'Skal jeg genfortælle alle detaljer om traumet?',
        a: 'Nej. Traumeterapi hos mig foregår skånsomt og doseret. Vi arbejder med kroppens signaler og nervesystemets regulering, så du hele tiden bevarer fodfæstet.',
      },
    ],
    proces_trin: [
      { step: '1', title: 'Gratis afklaringssamtale', subtitle: '20 min. uforpligtende', desc: 'Vi afklarer din situation og sikrer et trygt og stabilt fundament før eventuelt traumesamarbejde.' },
      { step: '2', title: 'Første session', subtitle: '60 min. i trygt rum', desc: 'Etablering af kropslige sikkerhedsankre og ressourcer i dit nervesystem (TBT-tilgang).' },
      { step: '3', title: 'Dit videre forløb', subtitle: 'I dit eget tempo', desc: 'Skånsom, doseret forløsning af chok og uforløste oplevelser, så fortiden endelig falder til ro.' },
    ],
    billede: 'https://assets.auxodev.dk/assets/ece5dc05-bafb-437f-b627-764ef307b8ad/img-4515.jpg',
    meta_title: 'Traumeterapi København | Psykoterapeut Kim Palasz',
    meta_desc: 'Traumeterapi i København hos Psykoterapeut Kim Palasz (TBT & MPF). Skånsom forløsning af chok og traumer i trygge rammer. Book gratis samtale.',
  },

  'sorg-og-krise': {
    titel: 'Sorg & krise',
    h1: 'Står du midt i en livskrise eller en dyb sorg?',
    hero_undertekst: 'Få nærværende terapeutisk støtte til at bære tabet, bearbejde sorgen og finde fodfæste og mening igen.',
    genkendelse: `
      <p class="mb-3">Når livet forandres – fx ved dødsfald, skilsmisse, alvorlig sygdom, tab af arbejde eller en pludselig livskrise – kan fundamentet under dig forsvinde.</p>
      <p class="mb-3">Sorg kan føles som en fysisk smerte i brystet, lammende tomhed, vrede eller en følelse af, at ingen forstår, hvor tung byrden er at bære alene.</p>
      <p>Mange oplever at føle sig forkerte, hvis sorgen tager længere tid, end omgivelserne forventer.</p>
    `,
    oensket_situation: `
      <p class="mb-3">At kunne trække vejret igen uden den knugende knude i maven.</p>
      <p>At kunne mindes uden at blive knust, og gradvist mærke, at livet igen kan rumme lys, glæde, mening og nye retninger.</p>
    `,
    loesning: `
      <p class="mb-3">I terapien giver vi sorgen og krisen det rum, den behøver, uden at forcere noget.</p>
      <p>Vi arbejder med både sorg, identitet og dine indre ressourcer, så du skridt for skridt kan integrere tabet og genfinde retning og livsmod i dit eget tempo.</p>
    `,
    fordele: `
      <ul class="space-y-2.5">
        <li class="flex items-start gap-2.5"><i class="fa-solid fa-check text-emerald-600 mt-1 shrink-0"></i><span>Et hjertevarmt og trygt rum, hvor alle følelser og tårer er velkomne</span></li>
        <li class="flex items-start gap-2.5"><i class="fa-solid fa-check text-emerald-600 mt-1 shrink-0"></i><span>Hjælp til at integrere tabet og finde fodfæste i en forandret tilværelse</span></li>
        <li class="flex items-start gap-2.5"><i class="fa-solid fa-check text-emerald-600 mt-1 shrink-0"></i><span>Aflastning fra skyldfølelse, ensomhed og overvældelse</span></li>
        <li class="flex items-start gap-2.5"><i class="fa-solid fa-check text-emerald-600 mt-1 shrink-0"></i><span>Genopdagelse af håb og nye meningsfulde perspektiver for fremtiden</span></li>
      </ul>
    `,
    testimonial: {
      name: 'Søren, 52 år',
      text: 'Jeg fik hjælp til at finde klarhed i en svær tid, hvor jeg følte mig fastlåst. Kims indsigt og guidning har ikke kun løst de problemer, jeg kom med, men også givet mig værktøjer til at skabe forandringer i mit liv. Jeg kan varmt anbefale ham.',
    },
    faq: [
      {
        q: 'Hvor længe varer en sorgproces?',
        a: 'Sorg har ingen udløbsdato og følger ikke en fast tidsplan. Terapi handler ikke om at skynde sig igennem sorgen, men om at give den plads, så den ikke låser dig fast i hverdagen.',
      },
      {
        q: 'Hvad hvis jeg føler mig helt følelsesløs efter et tab?',
        a: 'Følelsesmæssig lammelse er en naturlig beskyttelsesmekanisme ved chok og sorg. Vi arbejder blidt med at genskabe kontakten til kroppen, når du er klar.',
      },
    ],
    proces_trin: [
      { step: '1', title: 'Gratis afklaringssamtale', subtitle: '20 min. uforpligtende', desc: 'Vi taler stilfærdigt om dit tab eller din krise og dit behov for nærværende støtte.' },
      { step: '2', title: 'Første session', subtitle: '60 min. i trygt rum', desc: 'Et hjertevarmt rum til at udtrykke det tunge, sætte ord på følelserne og finde de første holdepunkter.' },
      { step: '3', title: 'Dit videre forløb', subtitle: 'I dit eget tempo', desc: 'Omsorgsfuld bearbejdning tilpasset din personlige sorgproces, så du gradvist kan finde fodfæste igen.' },
    ],
    billede: 'https://assets.auxodev.dk/assets/ece5dc05-bafb-437f-b627-764ef307b8ad/img-4518.jpg',
    meta_title: 'Sorg og krise terapi København | Psykoterapeut Kim Palasz',
    meta_desc: 'Få støtte ved sorg, skilsmisse og livskriser hos Psykoterapeut Kim Palasz på Amager. Nærværende hjælp til at finde fodfæste. Book gratis samtale.',
  },

  'hypnose-vaner': {
    titel: 'Vaner & hypnose',
    h1: 'Vil du bryde en fastgroet vane og genvinde kontrollen?',
    hero_undertekst: 'Effektiv hypnoterapi til rygestop, nikotin- og sukkertrang samt uhensigtsmæssige vaner. 2-3 sessioner mod varig forandring.',
    genkendelse: `
      <p class="mb-3">Kæmper du med vaner, du gerne vil bryde – fx rygning, snus, vaping, sukkertrang, overspisning eller andre fastlåste mønstre?</p>
      <p class="mb-3">Selvom din sunde fornuft ved, hvad der er bedst, overtager de automatiske vaner ofte i stressede stunder. Det skyldes, at vaner er lagret i det ubevidste sind, hvor ren viljestyrke sjældent rækker.</p>
      <p>Hypnose omprogrammerer de ubevidste triggere, så du slipper kampen mod dig selv.</p>
    `,
    oensket_situation: `
      <p class="mb-3">At føle dig fri for trangen og have kontrollen over dine valg tilbage.</p>
      <p>At kunne sidde i pausen eller ved en fest uden trang til cigaret eller snus, og at have overskud, sundhed og energi i hverdagen.</p>
    `,
    loesning: `
      <p class="mb-3">Hypnoterapi er en tryg, terapeutisk metode, hvor vi arbejder direkte med underbevidstheden for at skabe forandring og løse gamle mønstre.</p>
      <p>Du guides ind i en tilstand af dyb afslapning og fokus, hvor sindet bliver modtageligt for nye sunde valg, mens du hele tiden bevarer den fulde kontrol.</p>
    `,
    fordele: `
      <ul class="space-y-2.5">
        <li class="flex items-start gap-2.5"><i class="fa-solid fa-check text-emerald-600 mt-1 shrink-0"></i><span>Direkte adgang til underbevidsthedens vaner og automatiske mønstre</span></li>
        <li class="flex items-start gap-2.5"><i class="fa-solid fa-check text-emerald-600 mt-1 shrink-0"></i><span>Hurtig og effektiv omkodning af trang til nikotin, sukker eller overspisning</span></li>
        <li class="flex items-start gap-2.5"><i class="fa-solid fa-check text-emerald-600 mt-1 shrink-0"></i><span>Fuld kontrol og bevidsthed under hele sessionen</span></li>
        <li class="flex items-start gap-2.5"><i class="fa-solid fa-check text-emerald-600 mt-1 shrink-0"></i><span>Varige vaneforandringer uden evig kamp mod din egen viljestyrke</span></li>
      </ul>
    `,
    testimonial: {
      name: 'Josefine, 26 år',
      text: 'Efter et forløb hos Kim har jeg lært at tackle min angst og ændre negative mønstre. Hans evne til at stille de rigtige spørgsmål og støtte mig i processen har gjort en stor forskel. Jeg føler mig stærkere og mere i balance.',
    },
    faq: [
      {
        q: 'Mister jeg kontrollen under hypnose?',
        a: 'Nej, absolut ikke. Du er ved fuld bevidsthed hele tiden, kan høre og huske alt hvad der sker, og du kan til enhver tid åbne øjnene eller sige stop.',
      },
      {
        q: 'Hvor mange sessioner skal jeg bruge til rygestop eller sukkertrang?',
        a: 'Typisk tager et forløb til rygestop/nikotinstop eller sukkertrang 2-3 fokuserede sessioner (pakke til 3.000 kr. inkl. moms).',
      },
      {
        q: 'Kan alle hypnotiseres?',
        a: 'Stort set alle med et ønske om forandring kan guides ind i den behagelige, fokuserede tilstand af afslapning, som hypnose er.',
      },
    ],
    proces_trin: [
      { step: '1', title: 'Gratis afklaringssamtale', subtitle: '20 min. uforpligtende', desc: 'Vi afklarer din uønskede vane, motivation og sikrer, at hypnose er den rette løsning for dig.' },
      { step: '2', title: 'Første session', subtitle: 'Dybdegående hypnose', desc: 'Grundig forberedelse, afdækning af ubevidste triggere og den første fokuserede hypnoserejse.' },
      { step: '3', title: 'Opfølgende sessioner', subtitle: '2-3 sessioner i alt', desc: 'Forankring af de nye sunde mønstre i underbevidstheden, så forandringen bliver varig uden evig kamp.' },
    ],
    billede: 'https://assets.auxodev.dk/assets/ece5dc05-bafb-437f-b627-764ef307b8ad/img-4519.jpg',
    meta_title: 'Hypnoterapi & vaner København | Kim Palasz',
    meta_desc: 'Hypnoterapi på Amager i København hos Psykoterapeut Kim Palasz. Slip nikotin, sukkertrang og dårlige vaner på 2-3 sessioner. Book gratis samtale.',
  },
};

export default function ProblemPage({ fixedSlug }) {
  const params = useParams();
  const slug = fixedSlug || params.slug;
  const [data, setData] = useState(FALLBACK_PROBLEMS[slug] || null);
  const [isGuaranteeOpen, setIsGuaranteeOpen] = useState(false);

  useEffect(() => {
    // 1. Immediately reset data for this slug to prevent any stale state or flash of wrong content
    if (FALLBACK_PROBLEMS[slug]) {
      setData(FALLBACK_PROBLEMS[slug]);
    } else {
      setData(null);
    }

    // 2. Fetch specific problem item by slug from CMS
    fetch(`https://api.dev.auxo.dk/public/by-slug/${slug}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((item) => {
        if (item && item.titel) {
          setData((prev) => ({
            ...prev,
            titel: item.titel || prev?.titel,
            h1: item.h1 || prev?.h1,
            hero_undertekst: item.hero_undertekst || prev?.hero_undertekst,
            genkendelse: item.genkendelse || prev?.genkendelse,
            oensket_situation: item.oensket_situation || prev?.oensket_situation,
            loesning: item.loesning || prev?.loesning,
            fordele: item.fordele || prev?.fordele,
            testimonial: item.relevant_testimonial_tekst
              ? { name: item.relevant_testimonial_navn, text: item.relevant_testimonial_tekst }
              : prev?.testimonial,
            billede: (item.billede && item.billede[0]) || prev?.billede,
            meta_title: item.seo_title || prev?.meta_title,
            meta_desc: item.meta_description || prev?.meta_desc,
          }));
        }
      })
      .catch((err) => console.error('CMS problem load error:', err));
  }, [slug]);

  // Update document title and meta description
  useEffect(() => {
    if (data?.meta_title) {
      document.title = data.meta_title;
    }
  }, [data]);

  if (!data) {
    return (
      <div className="py-32 text-center max-w-xl mx-auto px-4">
        <h1 className="font-display text-3xl text-[#5A232E] mb-4">Siden blev ikke fundet</h1>
        <p className="text-sm text-[#6c7480] mb-6">Det efterspurgte problemområde findes ikke.</p>
        <Link to="/" className="px-6 py-3 bg-[#5A232E] text-white rounded-xl text-sm font-medium">
          Tilbage til forsiden
        </Link>
      </div>
    );
  }

  return (
    <div className="bg-[#ECEEF1] text-[#11161d]">
      {/* ─── 1. HERO SECTION: Dedicated Google Ads & conversion landing hero ─── */}
      <section className="pt-12 pb-16 sm:pt-16 sm:pb-24 border-b border-[#D9DDE2] relative overflow-hidden bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7 space-y-6 text-left">
              {/* Breadcrumb / Category indicator */}
              <div className="flex items-center gap-2 text-xs font-semibold text-[#6c7480]">
                <Link to="/" className="hover:text-[#5A232E]">Forside</Link>
                <span>/</span>
                <span className="text-[#5A232E] uppercase tracking-wider">{data.titel}</span>
              </div>

              {/* H1: Outcome-focused specific to problem */}
              <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#5A232E] tracking-tight leading-[1.15]">
                {data.h1}
              </h1>

              {/* Subtitle */}
              <p className="text-base sm:text-lg text-[#6c7480] leading-relaxed max-w-xl">
                {data.hero_undertekst}
              </p>

              {/* Dominant Primary CTA */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 pt-2">
                <a
                  href={BOOKING_SCREENING_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackEvent('cta_booking_click', { location: `problem_hero_${slug}` })}
                  className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-xl bg-[#5A232E] hover:bg-[#431722] text-white font-semibold text-base shadow-lg transition-all transform hover:-translate-y-0.5 no-break whitespace-nowrap cursor-pointer"
                >
                  <i className="fa-solid fa-calendar-check" aria-hidden="true" />
                  <span>Book en gratis afklaringssamtale</span>
                </a>
              </div>

              {/* Trust signals */}
              <div className="pt-4 border-t border-[#D9DDE2] flex flex-wrap items-center gap-4 sm:gap-6 text-xs text-[#6c7480]">
                <span className="flex items-center gap-1.5 font-medium text-[#5A232E]">
                  <i className="fa-solid fa-award text-[#5A232E]" aria-hidden="true" />
                  12 års erfaring
                </span>
                <span className="flex items-center gap-1.5 font-medium text-[#5A232E]">
                  <i className="fa-solid fa-users text-[#5A232E]" aria-hidden="true" />
                  500+ hjulpet
                </span>
                <button
                  type="button"
                  onClick={() => setIsGuaranteeOpen(true)}
                  className="flex items-center gap-1.5 font-medium text-[#5A232E] underline hover:text-[#5A232E] cursor-pointer"
                >
                  <i className="fa-solid fa-shield-check text-[#5A232E]" aria-hidden="true" />
                  100% tilfredshedsgaranti
                </button>
              </div>
            </div>

            {/* Right: Kim in practice photo */}
            <div className="lg:col-span-5">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl border-4 border-white bg-[#D9DDE2]">
                <img
                  src={optimizeImage(data.billede, 800)}
                  alt={`Kim Palasz behandler ${data.titel.toLowerCase()} i København`}
                  className="w-full h-auto object-cover max-h-[460px]"
                />
                <div className="absolute bottom-3 left-3 right-3 bg-white/95 backdrop-blur-md p-3 rounded-xl border border-[#D9DDE2] text-xs flex items-center justify-between">
                  <span className="font-medium text-[#5A232E]">Psykoterapeut Kim Palasz (MPF)</span>
                  <span className="text-[#5A232E]">Amager & Online</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── 2. GENKENDELSE: "Det her handler om mig" ─── */}
      <section className="py-16 sm:py-20 bg-[#ECEEF1] border-b border-[#D9DDE2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-3xl p-8 sm:p-12 border border-[#D9DDE2] shadow-xs">
            <span className="text-xs uppercase tracking-widest font-semibold text-[#5A232E] block mb-3">
              Genkendelse
            </span>
            <h2 className="font-display text-2xl sm:text-3xl text-[#5A232E] mb-6">
              Kan du genkende dette i din hverdag?
            </h2>
            <div
              className="text-sm sm:text-base text-[#11161d] leading-relaxed space-y-4"
              dangerouslySetInnerHTML={{ __html: data.genkendelse }}
            />
          </div>
        </div>
      </section>

      {/* ─── 3. ØNSKET SITUATION: Hvad længes du efter? ─── */}
      <section className="py-16 sm:py-20 bg-[#D9DDE2] border-b border-[#D9DDE2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-3xl p-8 sm:p-12 border border-[#D9DDE2] shadow-xs">
            <span className="text-xs uppercase tracking-widest font-semibold text-emerald-800 block mb-3">
              Det liv du ønsker
            </span>
            <h2 className="font-display text-2xl sm:text-3xl text-[#5A232E] mb-6">
              Forestil dig hverdagen, når {data.titel.toLowerCase()} fylder mindre
            </h2>
            <div
              className="text-sm sm:text-base text-[#11161d] leading-relaxed space-y-4"
              dangerouslySetInnerHTML={{ __html: data.oensket_situation }}
            />
          </div>
        </div>
      </section>

      {/* ─── 4. HVORDAN KIM ARBEJDER: Problem først, metode bagefter ─── */}
      <section className="py-16 sm:py-20 bg-white border-b border-[#D9DDE2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="text-xs uppercase tracking-widest font-semibold text-[#5A232E] block mb-3">
            Tilgang & Faglighed
          </span>
          <h2 className="font-display text-2xl sm:text-3xl text-[#5A232E] mb-6">
            Hvordan et forløb tilrettelægges
          </h2>
          <div
            className="text-sm sm:text-base text-[#11161d] leading-relaxed space-y-4"
            dangerouslySetInnerHTML={{ __html: data.loesning }}
          />
        </div>
      </section>

      {/* ─── 5. MULIGT UDBYTTE: Hvad klienten kan arbejde hen imod ─── */}
      <section className="py-16 sm:py-20 bg-[#ECEEF1] border-b border-[#D9DDE2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-3xl p-8 sm:p-12 border border-[#D9DDE2] shadow-xs">
            <span className="text-xs uppercase tracking-widest font-semibold text-[#5A232E] block mb-3">
              Mål & Forandring
            </span>
            <h2 className="font-display text-2xl sm:text-3xl text-[#5A232E] mb-6">
              Hvad kan et forløb hjælpe dig med at opnå?
            </h2>
            <div
              className="text-sm sm:text-base text-[#11161d] leading-relaxed"
              dangerouslySetInnerHTML={{ __html: data.fordele }}
            />
          </div>
        </div>
      </section>

      {/* ─── SÆRLIG FOR VANER: Konkrete hypnosebehandlinger (Rygestop & Sukkertrang) ─── */}
      {slug === 'hypnose-vaner' && (
        <section className="py-16 sm:py-20 bg-[#ECEEF1] border-b border-[#D9DDE2]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">
            <div className="max-w-3xl">
              <span className="text-xs uppercase tracking-widest font-semibold text-[#5A232E] block mb-2">
                Målrettede Forløb
              </span>
              <h2 className="font-display text-3xl sm:text-4xl text-[#5A232E] tracking-tight">
                Hypnose til rygestop, sukkertrang & vaner
              </h2>
              <p className="mt-3 text-base text-[#6c7480] leading-relaxed">
                Herunder kan du læse mere om de to målrettede hypnoseforløb til at slippe nikotin eller uønsket sukkertrang og madvaner.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Behandling 1: Rygestop, vaping og snus */}
              <div className="bg-white rounded-3xl p-8 sm:p-10 border border-[#D9DDE2] shadow-xs flex flex-col justify-between">
                <div className="space-y-4">
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#5A232E]/10 text-[#5A232E] text-xs font-semibold uppercase tracking-wider">
                    <i className="fa-solid fa-ban-smoking" aria-hidden="true" />
                    Nikotinstop
                  </div>
                  <h3 className="font-display text-2xl sm:text-3xl text-[#5A232E]">
                    Hypnose til rygestop, vaping og snus
                  </h3>
                  <div className="text-sm sm:text-base text-[#11161d] space-y-3 leading-relaxed">
                    <p>
                      Vil du gerne stoppe med nikotin, men oplever, at trangen eller vanen bliver ved med at vende tilbage?
                    </p>
                    <p>
                      Med hypnose arbejder vi med de automatiske mønstre og vaner, der kan være forbundet med rygning, vaping, snus og andre nikotinprodukter.
                    </p>
                    <p>
                      Målet er at gøre det lettere at slippe nikotinen, så du ikke hele tiden skal bruge viljestyrke på at holde fast i din beslutning.
                    </p>
                  </div>
                </div>

                <div className="mt-8 pt-6 border-t border-[#D9DDE2] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div className="text-xs text-[#6c7480]">
                    <span className="font-bold text-[#5A232E] text-base block">3.000 kr.</span>
                    2–3 sessioner (inkl. moms)
                  </div>
                  <a
                    href={BOOKING_THERAPY_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackEvent('cta_booking_click', { location: 'problem_vaner_rygestop' })}
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-[#5A232E] hover:bg-[#431722] text-white font-semibold text-xs transition-colors"
                  >
                    <i className="fa-solid fa-calendar-check" aria-hidden="true" />
                    <span>Book rygestopforløb</span>
                  </a>
                </div>
              </div>

              {/* Behandling 2: Sukkertrang og usunde madvaner */}
              <div className="bg-white rounded-3xl p-8 sm:p-10 border border-[#D9DDE2] shadow-xs flex flex-col justify-between">
                <div className="space-y-4">
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#5A232E]/10 text-[#5A232E] text-xs font-semibold uppercase tracking-wider">
                    <i className="fa-solid fa-cookie-bite" aria-hidden="true" />
                    Madvaner & Trang
                  </div>
                  <h3 className="font-display text-2xl sm:text-3xl text-[#5A232E]">
                    Hypnose mod sukkertrang og usunde madvaner
                  </h3>
                  <div className="text-sm sm:text-base text-[#11161d] space-y-3 leading-relaxed">
                    <p>
                      Sukkertrang og usunde madvaner handler ikke altid om sult eller manglende disciplin. For mange bliver bestemte måder at spise på automatiske vaner.
                    </p>
                    <p>
                      Med hypnose arbejder vi med de mønstre og den trang, der kan få dig til at række ud efter sukker, snacks eller usund mad – selvom du egentlig ønsker noget andet.
                    </p>
                    <p>
                      Målet er, at trangen fylder mindre, og at du får større frihed til selv at vælge.
                    </p>
                  </div>
                </div>

                <div className="mt-8 pt-6 border-t border-[#D9DDE2] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div className="text-xs text-[#6c7480]">
                    <span className="font-bold text-[#5A232E] text-base block">3.000 kr.</span>
                    2–3 sessioner (inkl. moms)
                  </div>
                  <a
                    href={BOOKING_THERAPY_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackEvent('cta_booking_click', { location: 'problem_vaner_sukkertrang' })}
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-[#5A232E] hover:bg-[#431722] text-white font-semibold text-xs transition-colors"
                  >
                    <i className="fa-solid fa-calendar-check" aria-hidden="true" />
                    <span>Book forløb mod sukkertrang</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ─── 6. RELEVANT TESTIMONIAL: Placér tæt på beslutningspunkt ─── */}
      {data.testimonial && (
        <section className="py-16 sm:py-20 bg-[#D9DDE2] border-b border-[#D9DDE2]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white rounded-3xl p-8 sm:p-10 border border-[#D9DDE2] shadow-md text-center max-w-4xl mx-auto">
              <div className="flex justify-center text-amber-500 gap-1 mb-4 text-sm">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="fa-solid fa-star" aria-hidden="true" />
                ))}
              </div>
              <blockquote className="font-display text-lg sm:text-xl text-[#5A232E] italic leading-relaxed mb-6">
                "{data.testimonial.text}"
              </blockquote>
              <div className="font-semibold text-sm text-[#5A232E]">{data.testimonial.name}</div>
              <div className="text-xs text-[#6c7480] mt-0.5">Klient hos Kim Palasz</div>

              {/* Mid-page booking CTA right after proof */}
              <div className="mt-8 pt-6 border-t border-[#D9DDE2]">
                <a
                  href={BOOKING_SCREENING_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackEvent('cta_booking_click', { location: `problem_testimonial_${slug}` })}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-[#5A232E] hover:bg-[#431722] text-white font-semibold text-sm transition-all shadow-md no-break whitespace-nowrap"
                >
                  <i className="fa-solid fa-calendar-check" aria-hidden="true" />
                  <span>Book gratis afklaringssamtale</span>
                </a>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ─── 7. SÅDAN FOREGÅR DET: 3 enkle trin ─── */}
      <section className="py-16 sm:py-20 bg-white border-b border-[#D9DDE2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="text-xs uppercase tracking-widest font-semibold text-[#5A232E] block mb-2">
              Processen
            </span>
            <h2 className="font-display text-2xl sm:text-3xl text-[#5A232E]">
              Sådan foregår dit forløb
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {(data.proces_trin || FALLBACK_PROBLEMS[slug]?.proces_trin || [
              { step: '1', title: 'Gratis afklaringssamtale', subtitle: '20 min. uforpligtende', desc: 'Vi taler kort om din situation, hvad du kæmper med, og du mærker om kemien er den rette.' },
              { step: '2', title: 'Første session', subtitle: '60 min. i trygt rum', desc: 'Vi afdækker dine specifikke udfordringer, etablerer tryghed og tilrettelægger din plan.' },
              { step: '3', title: 'Dit videre forløb', subtitle: 'I dit eget tempo', desc: 'Tilpasset samtale og øvelser, der skaber varig forandring og ro i nervesystemet.' },
            ]).map((trin, idx) => (
              <div key={idx} className="bg-[#ECEEF1] p-6 rounded-2xl border border-[#D9DDE2]">
                <div className="w-9 h-9 rounded-xl bg-[#5A232E] text-white font-bold flex items-center justify-center mb-4 text-sm">
                  {trin.step || idx + 1}
                </div>
                <h3 className="font-display text-lg text-[#5A232E] mb-1">{trin.title}</h3>
                <div className="text-xs text-[#5A232E] font-semibold mb-2">{trin.subtitle}</div>
                <p className="text-xs sm:text-sm text-[#6c7480] leading-relaxed">
                  {trin.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── 8. OM KIM PALASZ: Kort menneskelig introduktion ─── */}
      <section className="py-16 sm:py-20 bg-[#ECEEF1] border-b border-[#D9DDE2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-3xl p-8 sm:p-10 border border-[#D9DDE2] shadow-xs flex flex-col sm:flex-row items-center gap-8">
            <img
              src={optimizeImage(
                'https://assets.auxodev.dk/assets/ece5dc05-bafb-437f-b627-764ef307b8ad/profile-img.png',
                400
              )}
              alt="Psykoterapeut Kim Palasz"
              className="w-32 h-32 sm:w-40 sm:h-40 rounded-2xl object-cover shrink-0 border border-[#D9DDE2]"
            />
            <div className="space-y-3 text-left">
              <span className="text-xs uppercase tracking-wider font-semibold text-[#5A232E]">
                Din terapeut
              </span>
              <h3 className="font-display text-2xl text-[#5A232E]">
                Kim Palasz · Psykoterapeut MPF
              </h3>
              <p className="text-xs sm:text-sm text-[#6c7480] leading-relaxed">
                Siden 2014 har jeg drevet egen praksis på Amager. Jeg arbejder anerkendende og nærværende med fokus på at aktivere dine indre ressourcer og skabe et trygt rum, hvor du kan hele og udvikle dig.
              </p>
              <div className="pt-1 flex flex-wrap items-center gap-3 text-xs text-[#5A232E] font-medium">
                <span>12 års erfaring</span>
                <span>•</span>
                <span>500+ klienter</span>
                <span>•</span>
                <span>Uddannet i Traumeterapi (TBT) & Hypnoterapi</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── 9. PROBLEM-SPECIFIC FAQ ─── */}
      {data.faq && data.faq.length > 0 && (
        <section className="py-16 sm:py-20 bg-white border-b border-[#D9DDE2]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-display text-2xl sm:text-3xl text-[#5A232E] mb-8 text-center">
              Spørgsmål om {data.titel.toLowerCase()}
            </h2>
            <div className="space-y-4 max-w-4xl mx-auto">
              {data.faq.map((item, i) => (
                <div key={i} className="bg-[#ECEEF1] p-5 sm:p-6 rounded-2xl border border-[#D9DDE2]">
                  <h3 className="font-display font-medium text-base sm:text-lg text-[#5A232E] mb-2">
                    {item.q}
                  </h3>
                  <p className="text-xs sm:text-sm text-[#6c7480] leading-relaxed">
                    {item.a}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ─── 10. AFSLUTTENDE CTA SEKTION: Dominant konverteringsafslutning ─── */}
      <section className="py-16 sm:py-24 bg-[#5A232E] text-white text-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
          <span className="text-xs uppercase tracking-widest font-semibold text-[#D9DDE2] block">
            Næste skridt
          </span>
          <h2 className="font-display text-3xl sm:text-4xl text-white tracking-tight">
            Tag første skridt mod {data.titel.toLowerCase()} i dag
          </h2>
          <p className="text-sm sm:text-base text-[#ECEEF1]/80 max-w-xl mx-auto leading-relaxed">
            Klik nedenfor og vælg et tidspunkt, der passer dig. Samtalen varer 20 minutter over telefon eller online, og er 100% gratis og uforpligtende.
          </p>

          <div className="pt-4 flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href={BOOKING_SCREENING_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackEvent('cta_booking_click', { location: `problem_final_${slug}` })}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl bg-white hover:bg-[#ECEEF1] text-[#5A232E] font-semibold text-base shadow-xl no-break whitespace-nowrap cursor-pointer"
            >
              <i className="fa-solid fa-calendar-check" aria-hidden="true" />
              <span>Book en gratis afklaringssamtale</span>
            </a>
          </div>

          <p className="text-xs text-white/60 pt-2">
            Hvad sker der efter klik? Du vælger en tid i Kims kalender. Herefter modtager du en bekræftelse på mail/sms.
          </p>
        </div>
      </section>

      <SatisfactionGuaranteeModal
        isOpen={isGuaranteeOpen}
        onClose={() => setIsGuaranteeOpen(false)}
      />
    </div>
  );
}
